import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Box, Chip, CircularProgress, IconButton, Toolbar, Typography } from '@mui/material';
import { FC } from 'react';

import { CameraPicker } from '@components/CameraPicker';
import { DialogDarkFullscreen } from '@components/DialogDarkFullscreen';

import { useQrCamera } from './hooks/useQrCamera';
import { ScanFeedback } from './hooks/useQrScanFeedback';
import { sxAppBar, sxFeedbackChip, sxTitle, sxTopControls, sxVideoContainer, videoStyle } from './styles';

type QrScannerDialogProps = {
  open: boolean;
  title: string;
  onClose: VoidFunction;
  onQrCodeRecognized: (qrData: string) => void;
  onDecodeError?: VoidFunction;
  feedbackChip?: ScanFeedback | null;
  loading?: boolean;
  disableCloseButton?: boolean;
};

const QrScannerDialog: FC<QrScannerDialogProps> = ({
  open,
  title,
  onClose,
  onQrCodeRecognized,
  onDecodeError,
  feedbackChip = null,
  loading = false,
  disableCloseButton = false,
}) => {
  const { cameraDevices, setVideoRef, handleCameraChange } = useQrCamera({
    open,
    onScan: onQrCodeRecognized,
    onDecodeError,
  });

  const isVideoVisible = open && !loading;

  return (
    <DialogDarkFullscreen open={open} onClose={onClose}>
      <AppBar sx={sxAppBar}>
        <Toolbar>
          <IconButton
            color="inherit"
            disableRipple
            disabled={disableCloseButton}
            aria-label="Close scanner"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>

          <Typography sx={sxTitle} variant="h6" component="div">
            {title}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={sxVideoContainer(feedbackChip?.color)}>
        {!isVideoVisible ? (
          <CircularProgress aria-label="Loading..." />
        ) : (
          <>
            <Box sx={sxTopControls}>
              <Box />
              <CameraPicker cameras={cameraDevices} onCameraChange={handleCameraChange} />
            </Box>

            <video ref={setVideoRef} playsInline style={videoStyle}>
              <track kind="captions" />
            </video>

            {feedbackChip && (
              <Chip
                color={feedbackChip.color}
                icon={<feedbackChip.Icon />}
                label={feedbackChip.label}
                sx={sxFeedbackChip}
              />
            )}
          </>
        )}
      </Box>
    </DialogDarkFullscreen>
  );
};

export default QrScannerDialog;
